<template>
  <div class="grid justify-items-center">
    <vetster-logo-title :title="title" />
    <p
      class="mt-12 text-neutral-500 text-sm text-center"
      v-html="description"
    />
    <vetster-verification-code-input
      ref="codeInput"
      :code-length="codeLength"
      :error="error"
      :disabled="formLoading"
      class="mt-4"
      @complete="complete"
      @updated="updated"
    />
    <button
      v-if="!is2FA && !isEmailVerification"
      type="button"
      class="underline mt-4 text-ocean-950 text-sm"
      data-testid="login-manually-btn"
      @click.prevent="switchToPasswordLogin"
    >
      {{ $t('login.didnt-get-it') }}
      {{ $t('login.log-in-manually') }}
    </button>
    <button
      v-else-if="isEmailVerification"
      type="button"
      class="underline mt-4 text-ocean-950 text-sm"
      :class="formLoading ? 'cursor-not-allowed' : ''"
      @click.prevent="requestNewEmailVerificationCode"
    >
      {{ $t('login.didnt-get-it') }}
      {{ $t('login.send-me-new-code') }}
    </button>
    <vetster-loading
      class="mt-2"
      :is-loading="formLoading"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import VetsterLoading from '@/brand-components/VetsterLoading';
import VetsterLogoTitle from '@/brand-components/VetsterLogoTitle';
import VetsterVerificationCodeInput from '@/brand-components/VetsterVerificationCodeInput';
import AuthFormType from '@/enums/AuthFormType';
import Channel from '@/enums/ChannelType';
import CodeLength from '@/enums/CodeLength';
import ParseErrors from '@/mixins/ParseErrors';

export default {
  name: 'VetsterOneTimePasswordForm',

  components: {
    VetsterLoading,
    VetsterLogoTitle,
    VetsterVerificationCodeInput,
  },

  mixins: [ParseErrors],

  props: {
    channel: {
      type: String,
      default: '',
    },
    email: {
      type: String,
      default: '',
    },
    error: {
      type: String,
      default: '',
    },
    is2FA: {
      type: Boolean,
      default: false,
    },
    isEmailVerification: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    phoneNumber: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      isRequestingEmailVerification: false,
    };
  },

  computed: {
    ...mapGetters('emailVerification', ['getSocialite']),
    ...mapGetters('passwordlessLogin', ['getCodeLength']),
    title() {
      if (this.isEmailVerification) {
        return this.$t('login.confirm-your-email');
      }

      return this.$t('login.check-your-channel-for-a-one', { channel: this.channelText });
    },
    description() {
      if (this.isEmailVerification) {
        return this.$t('login.account-detected');
      }

      return this.$t('login.we-ve-sent-a-code', {
        codeLength: this.codeLength,
        recipient: this.recipient,
      });
    },
    recipient() {
      switch (this.channel) {
        case Channel.Sms:
          return this.phoneNumber;
        case Channel.Email:
        default:
          return this.email;
      }
    },
    channelText() {
      switch (this.channel) {
        case Channel.Sms:
          return 'SMS';
        case Channel.Email:
        default:
          return 'email';
      }
    },
    codeLength() {
      if (this.is2FA) {
        return CodeLength.TwoFA;
      }

      if (this.isEmailVerification) {
        return CodeLength.EmailVerification;
      }

      return this.getCodeLength;
    },
    getResendPayload() {
      return {
        provider_type: this.getSocialite?.provider_type,
        access_token: this.getSocialite?.access_token,
      };
    },
    formLoading() {
      return this.isLoading || this.isRequestingEmailVerification;
    },
  },

  mounted() {
    this.$refs.codeInput.focusNextEmptyField();
  },

  methods: {
    complete(code) {
      this.$emit('complete', code);
    },
    updated(code) {
      this.$emit('updated', code);
    },
    switchToPasswordLogin() {
      setTimeout(() => {
        this.$emit('switch', AuthFormType.PasswordLogin);
      }, 100);
    },
    async requestNewEmailVerificationCode() {
      if (!this.isRequestingEmailVerification) {
        this.isRequestingEmailVerification = true;
        this.$auth.requestEmailVerificationCode(this.getResendPayload)
          .then(() => {
            this.$toast(this.$t('login.resent-an-email-verification'));
          })
          .catch((error) => {
            this.parseError(error, true);
          })
          .finally(() => {
            this.isRequestingEmailVerification = false;
          });
      }
    },
  },
};
</script>
